import { DndContext, DragMoveEvent, DragStartEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { Range } from 'types'
import { MediaTrimmerWithoutContext } from '../../media-trimmer'
import { UGEContext } from '../context'
import Playhead from './playhead'
import { Container } from './style'

type Props = {
    className?: string
}

const UGETrimmer = ({ className }: Props) => {
    const pausedStateRef = useRef<boolean>(false)
    const { duration, paused, reversed, scrub, source, sourceInfo, trim, setPaused, setTrim } = useContext(UGEContext)
    const [playheadTrim, setPlayheadTrim] = useState<Range>(trim)
    const pointerSensor = useSensor(PointerSensor)
    const sensors = useSensors(pointerSensor)

    const loops = Math.max(1, Math.ceil(duration / (sourceInfo?.duration || 0)))
    const timing = useMemo(
        () => ({
            start: trim.start * duration,
            end: trim.end * duration,
        }),
        [duration, trim]
    )

    function onChange(timing: Range) {
        setTrim({
            start: Math.round((timing.start / duration) * 100) / 100,
            end: Math.round((timing.end / duration) * 100) / 100,
        })
    }

    function onDragMove(e: DragMoveEvent | DragStartEvent) {
        if (!e.active.data.current) return

        const { controlType, controlValue } = e.active.data.current

        switch (controlType) {
            case 'range-end':
                const end = (controlValue as Range).end
                setPlayheadTrim({ ...trim, end })
                scrub(end)
                break
            case 'range-start':
                const start = (controlValue as Range).start
                setPlayheadTrim({ ...trim, start })
                scrub(start)
                break
            case 'playhead':
                const value = (controlValue as number) * (trim.end - trim.start)
                scrub(trim.start + value)
                break
        }
    }

    function onDragStart(e: DragStartEvent) {
        pausedStateRef.current = paused
        setPaused(true)
        onDragMove(e)
    }

    function onDragEnd() {
        setPaused(pausedStateRef.current)
    }

    useEffect(() => {
        setPlayheadTrim(trim)
    }, [trim])

    if (!duration || !sourceInfo?.duration) {
        return null
    }

    return (
        <Container className={className}>
            <DndContext sensors={sensors} onDragStart={onDragStart} onDragMove={onDragMove} onDragEnd={onDragEnd}>
                <MediaTrimmerWithoutContext
                    duration={duration}
                    loops={loops}
                    reversed={reversed}
                    size="x-large"
                    source={source}
                    timing={timing}
                    onChange={onChange}
                >
                    <Playhead trim={playheadTrim} />
                </MediaTrimmerWithoutContext>
            </DndContext>
        </Container>
    )
}

export default UGETrimmer
